import React, { Component } from 'react'
import { EditorState, SelectionState } from 'draft-js'
import intl from 'react-intl-universal'
import { is } from 'immutable'
import { Subscription } from 'rxjs'
import { debounce, isBoolean } from 'lodash'
import { collapseSelection } from '../../../pluginCommon/pluginHelpers'
import {
  createAnnotationAtSelection,
  createSelectOptions,
  getCurrentEntity,
} from '../../editorUtils'
import { Form } from 'reactstrap'
import { connect } from 'react-redux'
import CloseIcon from '../../icons/Close.js'
import { validateAnnotationSquema } from '~/reactapp/shared/annotations/annotation.service'
import {
  AnnotationFormValue,
  AnnotationsModalProps,
  AnnotationsModalState,
} from '../../types'
import { AnnotationType } from '~/types/annotation'
import ActionButtons from './ActionButtons'
import SelectFields from './SelectFields'

class AnnotationsModal extends Component<
  AnnotationsModalProps,
  AnnotationsModalState
> {
  subscription?: Subscription | null
  selection?: SelectionState

  constructor(props) {
    super(props)
    this.state = {
      hasFocus: false,
      annotationTypes: [],
      annotationItems: undefined,
      annotationChildren: undefined,
      annotationFormData: {},
      description: undefined,
      categoryLoading: false,
      schemaError: false,
      formTouched: false,
    }
    this.setHasFocus = debounce(this.setHasFocus)
  }

  componentDidMount() {
    this.subscription = this.props.formSubject.subscribe(this.onFormStateChange)
    this.setAnnotationTypes()
    this.createModalObserver()
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    this.setHasFocus.cancel()
  }

  // Retrive the annotations from the settings (redux) and set the state
  setAnnotationTypes = () => {
    const { annotations } = this.props
    if (!annotations || annotations.length === 0) return
    const annotationTypes = annotations.map((annotation) => ({
      value: annotation,
      label: annotation.name,
    }))
    if (this.props.isEditting) {
      this.setState({
        annotationItems: undefined,
        annotationChildren: undefined,
        annotationFormData: {},
        annotationTypes: annotationTypes,
        description: undefined,
        categoryLoading: true,
        schemaError: false,
      })
      this.setDataFrom(annotationTypes)
    } else {
      this.setState({ annotationTypes })
    }
  }

  // Set the data when the user is editing an annotation
  setDataFrom = async (
    annotationTypesOptions: Array<AnnotationFormValue<AnnotationType>>
  ) => {
    // Retrieve the data from the current entity
    const editorState = this.props.getEditorState()
    const entity = getCurrentEntity(editorState)
    if (!entity || !entity.data) return
    const { categoryId, itemId, childId } = entity.data

    // Set the annotation type select
    const annotationType = annotationTypesOptions.find(
      (option) => option.value.categoryId === categoryId
    )
    if (!annotationType) {
      this.setState({
        annotationItems: undefined,
        annotationChildren: undefined,
        annotationFormData: {},
        annotationTypes: annotationTypesOptions,
        description: undefined,
        categoryLoading: false,
        schemaError: false,
      })
      return
    }
    const userAnnotationData = await validateAnnotationSquema(
      annotationType.value.link
    )
    if (!isBoolean(userAnnotationData)) {
      if (!userAnnotationData || userAnnotationData.items?.length === 0) {
        this.setState({
          annotationItems: [],
          annotationFormData: {
            category: annotationType,
          },
          annotationChildren: undefined,
          annotationTypes: annotationTypesOptions,
          description: undefined,
          categoryLoading: false,
          schemaError: false,
        })
      } else {
        // Set the annotation item select
        const annotationItems = createSelectOptions(
          userAnnotationData.items,
          'title'
        )
        const itemFormValue = annotationItems.find(
          (item) => item.value.id === itemId
        )

        if (!itemFormValue) {
          // Item not found
          this.setState({
            annotationItems,
            annotationFormData: {
              category: annotationType,
            },
            annotationChildren: undefined,
            annotationTypes: annotationTypesOptions,
            description: undefined,
            categoryLoading: false,
            schemaError: false,
          })
        } else {
          // Set the annotation child select
          if (
            itemFormValue.value.children &&
            itemFormValue.value.children.length > 0
          ) {
            const annotationChildren = createSelectOptions(
              itemFormValue.value.children,
              'title'
            )
            const childFormValue = annotationChildren.find(
              (child) => child.value.id === childId
            )
            if (!childFormValue) {
              // Child not found
              this.setState({
                annotationItems,
                annotationFormData: {
                  category: annotationType,
                  item: itemFormValue,
                },
                annotationChildren,
                annotationTypes: annotationTypesOptions,
                description: undefined,
                categoryLoading: false,
                schemaError: false,
              })
            } else {
              // All data found
              this.setState({
                annotationItems,
                annotationFormData: {
                  category: annotationType,
                  item: itemFormValue,
                  child: childFormValue,
                },
                annotationChildren: annotationChildren,
                annotationTypes: annotationTypesOptions,
                description: childFormValue.value.description,
                categoryLoading: false,
                schemaError: false,
              })
            }
          } else {
            // Item has no children, so we set the description
            this.setState({
              annotationItems,
              annotationFormData: {
                category: annotationType,
                item: itemFormValue,
              },
              annotationChildren: undefined,
              annotationTypes: annotationTypesOptions,
              description: itemFormValue.value.description,
              categoryLoading: false,
              schemaError: false,
            })
          }
        }
      }
    } else {
      this.setState({
        annotationItems: undefined,
        annotationChildren: undefined,
        annotationFormData: {},
        annotationTypes: annotationTypesOptions,
        description: undefined,
        categoryLoading: false,
        schemaError: true,
      })
    }
  }

  clearSelection = (editorState: EditorState) => {
    return EditorState.forceSelection(
      editorState,
      collapseSelection(editorState.getSelection())
    )
  }

  onClose = () => {
    this.subscription && this.subscription.unsubscribe()
    this.subscription = null
    this.props.updateInlineToolbar(false)
  }

  cancel = () => {
    this.onClose()
    this.props.setEditorState(this.clearSelection(this.props.getEditorState()))
  }

  onFormStateChange = (params) => {
    if (params && params.type === 'editor') {
      if (params.selection && params.selection.getHasFocus()) {
        // Don't close if it's just focus change (ex swtich back from another tab)
        if (
          !this.selection ||
          !is(params.selection, this.selection.set('hasFocus', true))
        ) {
          this.onClose()
        }
        // Don't close if users focus out to other tabs or application
      } else if (document.hasFocus()) {
        setTimeout(() => {
          if (!this.state.hasFocus) {
            this.onClose()
          }
        }, 150)
      }
      this.selection = params.selection
    }
  }

  onBlur = () => {
    this.setHasFocus(false)
  }

  onFocus = () => {
    this.setHasFocus(true)
  }

  setHasFocus = (hasFocus: boolean) => {
    this.setState({ hasFocus })
  }

  applyAnnotation = () => {
    this.subscription && this.subscription.unsubscribe()
    this.subscription = null
    const { getEditorState, setEditorState } = this.props
    const newEditorState = createAnnotationAtSelection(
      setEditorState,
      getEditorState,
      this.state.annotationFormData
    )

    setEditorState(this.clearSelection(newEditorState))
    this.onClose()
  }

  modalVisible = (entries) => {
    entries.forEach((entry) => {
      const toolbar = document.getElementById('draft-inlinetoolbar')
      const editorContainer = document.getElementById('editor-container')

      // If the modal is not completely visible and editor scroll value is less than 180px
      // (annotations in the first lines)
      // 120 represent a small scroll value. We don't need to modify anything if the user's
      // scroll value is more than 120px
      if (
        !entry.isIntersecting &&
        toolbar &&
        editorContainer &&
        editorContainer?.scrollTop < 120
      ) {
        // Force to show the annotation modal 200 px below
        toolbar.style.top = '-200px'
        toolbar.classList.add('removeIndicator')
      }
    })
  }

  createModalObserver = () => {
    const options = { root: null, threshold: 1 }
    const observer = new IntersectionObserver(this.modalVisible, options)
    const target = document.getElementById('create/update-annotation-modal')
    if (!target) return
    observer.observe(target)
  }

  render() {
    return (
      <Form
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        tabIndex={-1}
        className={this.props.className}
      >
        <div
          className="annotation-modal"
          data-testid="create/update-annotation-modal"
          id="create/update-annotation-modal"
        >
          <div className="annotation-modal-content">
            <div className="row justify-content-between m-0">
              <div>
                <h1 className="annotation-modal-title">
                  {this.props.isEditting
                    ? intl.get(
                        'annotationPlugin.modal.title.updateAnAnnotations'
                      )
                    : intl.get('annotationPlugin.modal.title.addAnAnnotations')}
                </h1>
                <span className="annotation-modal-subtitle">
                  {intl.get('annotationPlugin.modal.subtitle')}
                </span>
              </div>
              <button
                onClick={this.cancel}
                className="ads-btn-transparent"
                id="annotation-modal-close"
                data-toggle="tooltip"
                title="Close modal"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="annotation-modal-fields-title">
              {intl.get('annotationPlugin.modal.chooseCategory')}
            </div>

            <SelectFields
              setState={this.setState.bind(this)}
              state={this.state}
            />
          </div>

          <ActionButtons
            applyAnnotation={this.applyAnnotation}
            cancel={this.cancel}
            isEditting={this.props.isEditting}
            annotationFormData={this.state.annotationFormData}
            formTouched={this.state.formTouched}
          />
        </div>
      </Form>
    )
  }
}

function mapStateToProps(state) {
  const annotations = state.settings?.annotations
  return {
    annotations,
  }
}

export default connect(mapStateToProps)(AnnotationsModal)
